import { useCallback, useEffect, useState } from 'react'

export type DmpChatToolDisplayMode = 'name-only' | 'name-and-arguments' | 'full-details'

export const DMP_CHAT_TOOL_DISPLAY_MODE_STORAGE_KEY = 'dmp-chat-tool-display-mode'

export const DMP_CHAT_TOOL_DISPLAY_OPTIONS = [
  {
    value: 'name-only',
    label: 'Name only',
    description: 'Show tool names and lifecycle status.',
  },
  {
    value: 'name-and-arguments',
    label: 'Name and arguments',
    description: 'Show names, status, and validated arguments.',
  },
  {
    value: 'full-details',
    label: 'Full details',
    description: 'Show validated arguments and results.',
  },
] as const satisfies readonly {
  value: DmpChatToolDisplayMode
  label: string
  description: string
}[]

export function isDmpChatToolDisplayMode(value: unknown): value is DmpChatToolDisplayMode {
  return DMP_CHAT_TOOL_DISPLAY_OPTIONS.some((option) => option.value === value)
}

export function getDmpChatDefaultToolDisplayMode(): DmpChatToolDisplayMode {
  return import.meta.env.DEV ? 'full-details' : 'name-only'
}

export function useDmpChatToolDisplayMode(): {
  mode: DmpChatToolDisplayMode
  setMode: (mode: DmpChatToolDisplayMode) => void
} {
  const defaultMode = getDmpChatDefaultToolDisplayMode()
  const [mode, setMode] = useState<DmpChatToolDisplayMode>(defaultMode)

  useEffect(() => {
    setMode(readStoredToolDisplayMode(defaultMode))

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== DMP_CHAT_TOOL_DISPLAY_MODE_STORAGE_KEY) {
        return
      }

      setMode(readStoredToolDisplayMode(defaultMode))
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [defaultMode])

  const updateMode = useCallback((nextMode: DmpChatToolDisplayMode) => {
    setMode(nextMode)
    try {
      window.localStorage.setItem(DMP_CHAT_TOOL_DISPLAY_MODE_STORAGE_KEY, nextMode)
    } catch {
      // A restricted browser storage implementation should not block chat rendering.
    }
  }, [])

  return { mode, setMode: updateMode }
}

function readStoredToolDisplayMode(defaultMode: DmpChatToolDisplayMode): DmpChatToolDisplayMode {
  try {
    const storedMode = window.localStorage.getItem(DMP_CHAT_TOOL_DISPLAY_MODE_STORAGE_KEY)
    return isDmpChatToolDisplayMode(storedMode) ? storedMode : defaultMode
  } catch {
    return defaultMode
  }
}
